import './App.css';
import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { translations } from './translations';
import PrivacyPolicy from './PrivacyPolicy';

function HomePage({ t }) {
  return (
    <>
      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">
                <span className="title-main">{t.hero.title}</span>
                <span className="title-sub">{t.hero.subtitle}</span>
              </h1>
              <p className="hero-description">
                {t.hero.description}
              </p>
              <div className="hero-buttons">
                <a href="#download" className="btn btn-primary">
                  <span>{t.hero.downloadBtn}</span>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 14L6 10H8V6H12V10H14L10 14Z" fill="currentColor" />
                    <path d="M17 16H3V18H17V16Z" fill="currentColor" />
                  </svg>
                </a>
              </div>
            </div>
            <div className="hero-image">
              <div className="phone-mockup">
                <img src="/app-mockup.png" alt="Bousla App" className="app-screenshot" />
              </div>
            </div>
          </div>
        </div>
        <div className="hero-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">⚡</div>
              <h3>{t.features.fast.title}</h3>
              <p>{t.features.fast.desc}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">💰</div>
              <h3>{t.features.price.title}</h3>
              <p>{t.features.price.desc}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🌟</div>
              <h3>{t.features.service.title}</h3>
              <p>{t.features.service.desc}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📱</div>
              <h3>{t.features.easy.title}</h3>
              <p>{t.features.easy.desc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t.services.title}</h2>
            <p className="section-subtitle">{t.services.subtitle}</p>
          </div>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon">
                <div className="icon-circle">🚗</div>
              </div>
              <h3 className="service-title">{t.services.passenger.title}</h3>
              <p className="service-description">
                {t.services.passenger.desc}
              </p>
              <ul className="service-features">
                {t.services.passenger.features.map((feature, index) => (
                  <li key={index}>✓ {feature}</li>
                ))}
              </ul>
            </div>

            <div className="service-card featured">
              <div className="featured-badge">{t.services.delivery.badge}</div>
              <div className="service-icon">
                <div className="icon-circle">📦</div>
              </div>
              <h3 className="service-title">{t.services.delivery.title}</h3>
              <p className="service-description">
                {t.services.delivery.desc}
              </p>
              <ul className="service-features">
                {t.services.delivery.features.map((feature, index) => (
                  <li key={index}>✓ {feature}</li>
                ))}
              </ul>
            </div>

            <div className="service-card">
              <div className="service-icon">
                <div className="icon-circle">🚌</div>
              </div>
              <h3 className="service-title">{t.services.school.title}</h3>
              <p className="service-description">
                {t.services.school.desc}
              </p>
              <ul className="service-features">
                {t.services.school.features.map((feature, index) => (
                  <li key={index}>✓ {feature}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section id="download" className="download">
        <div className="container">
          <div className="download-content">
            <div className="download-text">
              <h2 className="download-title">{t.download.title}</h2>
              <p className="download-description">
                {t.download.desc}
              </p>
              <div className="download-buttons">
                <a href="https://apps.apple.com" target="_blank" rel="noopener noreferrer" className="store-button">
                  <div className="store-icon">
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                    </svg>
                  </div>
                  <div className="store-text">
                    <span className="store-label">{t.download.downloadFrom}</span>
                    <span className="store-name">{t.download.appStore}</span>
                  </div>
                </a>
                <a href="https://play.google.com" target="_blank" rel="noopener noreferrer" className="store-button">
                  <div className="store-icon">
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                    </svg>
                  </div>
                  <div className="store-text">
                    <span className="store-label">{t.download.downloadFrom}</span>
                    <span className="store-name">{t.download.googlePlay}</span>
                  </div>
                </a>
              </div>
            </div>
            <div className="download-image">
              <div className="phones-mockup">
                <div className="phone phone-1"></div>
                <div className="phone phone-2"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h3 className="footer-logo">بوصلة</h3>
              <p>{t.footer.tagline}</p>
            </div>
            <div className="footer-section">
              <h4>{t.footer.quickLinks}</h4>
              <ul>
                <li><a href="#home">{t.nav.home}</a></li>
                <li><a href="#services">{t.nav.services}</a></li>
                <li><a href="#download">{t.nav.download}</a></li>
                <li><Link to="/privacy-policy">{t.footer.privacy}</Link></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>{t.footer.contact}</h4>
              <ul className="contact-info">
                <li>📧 info@bousla.com</li>
                <li>📱 +963958855130</li>
              </ul>
              <div className="social-links">
                <a href="https://wa.me/963958855130" target="_blank" rel="noopener noreferrer" className="social-button whatsapp" aria-label="WhatsApp">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                  </svg>
                </a>
                <a href="https://www.facebook.com/bousla.sy" target="_blank" rel="noopener noreferrer" className="social-button facebook" aria-label="Facebook">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
                <a href="https://www.instagram.com/bousla.sy" target="_blank" rel="noopener noreferrer" className="social-button instagram" aria-label="Instagram">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>{t.footer.rights}</p>
          </div>
        </div>
      </footer>
    </>
  );
}

function App() {
  const [language, setLanguage] = useState('ar');
  const [direction, setDirection] = useState('rtl');

  const t = translations[language];

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = direction;
    document.body.style.direction = direction;
  }, [language, direction]);

  const changeLanguage = (lang) => {
    setLanguage(lang);
    setDirection(lang === 'ar' ? 'rtl' : 'ltr');
  };

  return (
    <Router>
      <div className="App">
        {/* Navigation */}
        <nav className="navbar">
          <div className="container">
            <div className="nav-content">
              <div className="logo">
                <Link to="/" style={{ textDecoration: 'none' }}>
                  <span className="logo-text">بوصلة</span>
                  <span className="logo-subtitle">BOUSLA</span>
                </Link>
              </div>
              <div className="nav-links">
                <Link to="/">{t.nav.home}</Link>
                <a href="/#services">{t.nav.services}</a>
                <a href="/#download">{t.nav.download}</a>

                {/* Language Switcher */}
                <div className="language-switcher">
                  <button
                    className={`lang-btn ${language === 'ar' ? 'active' : ''}`}
                    onClick={() => changeLanguage('ar')}
                  >
                    AR
                  </button>
                  <button
                    className={`lang-btn ${language === 'en' ? 'active' : ''}`}
                    onClick={() => changeLanguage('en')}
                  >
                    EN
                  </button>
                  <button
                    className={`lang-btn ${language === 'fr' ? 'active' : ''}`}
                    onClick={() => changeLanguage('fr')}
                  >
                    FR
                  </button>
                </div>
              </div>
            </div>
          </div>
        </nav>

        <Routes>
          <Route path="/" element={<HomePage t={t} />} />
          <Route path="/privacy-policy" element={
            <PrivacyPolicy t={t} />
          } />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
